import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Collapse,
  useTheme,
  useMediaQuery,
  Grid,
  Tooltip,
  Pagination,
  FormControl,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  Stack,
  Switch
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Assessment as AssessmentIcon,
  Photo as PhotoIcon,
  Image as ImageIcon,
  Edit as EditIcon,
  List as ListIcon,
  Close as CloseIcon,
  Badge as BadgeIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PictureAsPdf as PictureAsPdfIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import TextField from '@mui/material/TextField';

interface Response {
  response_id: string;
  safety_check_id: string;
  question_id: string;
  user_id: string;
  response_text: string;
  response_status: string;
  created_at: string;
  updated_at: string;
  user_name?: string;
  question_text?: string;
  question_type?: string;
  question_tags?: any;
  check_name?: string;
  safety_check_status?: string;
  safety_check_due_date?: string;
  location_name?: string;
  additional_text?: string;
  has_image?: boolean;
  images?: Array<{
    id: string;
    path: string;
    created_at: string;
  }>;
}

interface LinkedAsset {
  id: string;
  name: string;
  serial: string | null;
}

interface GroupedResponse {
  safety_check_id: string;
  check_id?: string | null;
  check_name: string;
  location_name: string;
  check_type?: string;
  latitude?: number | string;
  longitude?: number | string;
  safety_check_status: string;
  safety_check_due_date: string;
  discarded_at?: string | null;
  assigned_to_name?: string | null;
  asset_image_url?: string;
  asset_image_is_fallback?: boolean;
  scanned_sheet_image_url?: string;
  total_users: number;
  completed_users: number;
  linked_asset_ids?: string[];
  linked_assets?: LinkedAsset[];
  responses: Response[];
  analysis?: {
    analysis_text: string;
    created_at: string;
  };
}

interface Analysis {
  analysis_text: string;
  created_at: string;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface DvlaResult {
  driver: { name: string | null; dob: string | null; gender: string | null; licenseNumber: string | null };
  address: { line1: string | null; line2: string | null; line3: string | null; line4: string | null; line5: string | null; postcode: string | null };
  licence: { type: string | null; status: string | null; issueNumber: string | null; validFrom: string | null; validUntil: string | null; expiryDate?: string | null };
  pointsTotal: number;
  vehicleCategories: Array<{ code: string; type: string | null; description: string; legalLiteral: string; fromDate: string | null; expiryDate: string | null; restrictions: Array<{ code: string; literal: string }> }>;
  endorsements: Array<{ code: string; points: number; offenseDate: string | null; convictionDate: string | null; sentenceDate: string | null; disqualificationType: string | null; disqualificationYears: number | null; offenseLegalLiteral: string; courtReference: string | null }>;
  createdAt?: string;
}

interface OutOfActionAudit {
  audit_id: string;
  asset_id: string;
  business_id: string;
  user_id: string;
  action: 'marked_out_of_action' | 'marked_available';
  note: string;
  image_urls?: string;
  images?: string[];
  latitude?: number;
  longitude?: number;
  safety_check_id?: string;
  related_safety_check_id?: string;
  first_name: string;
  last_name: string;
  email: string;
  asset_name: string;
  created_at: string;
}

// Subtle check ID display with copy — for support/debugging
const CheckIdCopy: React.FC<{ checkId: string; label?: string }> = ({ checkId, label = 'Check ID' }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(checkId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };
  if (!checkId) return null;
  return (
    <Tooltip title={copied ? 'Copied!' : `Copy ${label} for support`} arrow placement="top">
      <Box
        component="span"
        onClick={handleCopy}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          fontSize: '0.7rem',
          fontFamily: 'monospace',
          color: 'text.disabled',
          cursor: 'pointer',
          '&:hover': { color: 'text.secondary' },
        }}
      >
        <ContentCopyIcon sx={{ fontSize: 12, opacity: 0.7 }} />
        <Box component="span" sx={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis' }}>{checkId}</Box>
      </Box>
    </Tooltip>
  );
};

// Component to render location with coordinates tooltip
const LocationDisplay: React.FC<{ 
  locationName: string; 
  latitude?: number | string; 
  longitude?: number | string; 
}> = ({ locationName, latitude, longitude }) => {
  // Convert to numbers if they're strings, handle null/undefined
  const lat = typeof latitude === 'string' ? parseFloat(latitude) : latitude;
  const lng = typeof longitude === 'string' ? parseFloat(longitude) : longitude;
  
  // Check if we have valid coordinates
  const hasCoordinates = lat !== null && lng !== null && lat !== undefined && lng !== undefined && !isNaN(lat) && !isNaN(lng);
  
  if (!hasCoordinates) {
    return <span>{locationName}</span>;
  }

  const coordinatesText = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`;
  
  return (
    <Tooltip title={coordinatesText} arrow placement="top">
      <span style={{ cursor: 'help', borderBottom: '1px dotted #ccc' }}>
        {locationName}
      </span>
    </Tooltip>
  );
};

// Component to display asset image for safety checks
const AssetImageDisplay: React.FC<{ 
  assetImageUrl?: string;
  checkName: string;
  isFallback?: boolean;
}> = ({ assetImageUrl, checkName, isFallback }) => {
  // Always show something, even if no image
  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', color: 'primary.main' }}>
        <ImageIcon fontSize="small" sx={{ mr: 0.5 }} />
        Asset Image
        {isFallback && (
          <Box component="span" sx={{ ml: 1, px: 0.75, py: 0.25, bgcolor: 'warning.light', color: 'warning.dark', borderRadius: 1, fontSize: '10px', fontWeight: 700 }}>
            Defaulted to asset photo
          </Box>
        )}
      </Typography>
      
      {assetImageUrl ? (
        <Box
          sx={{
            position: 'relative',
            width: 200,
            height: 150,
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid #e0e0e0',
            cursor: 'pointer',
            '&:hover': {
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            }
          }}
          onClick={() => window.open(assetImageUrl, '_blank')}
          title={`Click to view full size asset image for ${checkName}`}
        >
          <img
            src={assetImageUrl}
            alt={`Asset for ${checkName}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            onError={(e) => {
              // If image fails to load, show a placeholder
              const target = (e.target as HTMLImageElement);
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent) {
                parent.innerHTML = `
                  <div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background-color: #f5f5f5; color: #666;">
                    <div style="text-align: center;">
                      <div>🖼️</div>
                      <div style="font-size: 12px;">Asset image unavailable</div>
                    </div>
                  </div>
                `;
              }
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            width: 200,
            height: 150,
            borderRadius: 1,
            border: '2px dashed #ddd',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f9f9f9',
            color: '#999'
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <ImageIcon fontSize="large" sx={{ mb: 1, color: '#ccc' }} />
            <Typography variant="body2" color="text.secondary">
              No asset image
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};


// Component to display scanned paper check sheet
const ScannedSheetDisplay: React.FC<{ 
  scannedSheetUrl?: string;
  checkName: string;
}> = ({ scannedSheetUrl, checkName }) => {
  // Debug logging to help troubleshoot
  console.log('🖼️ ScannedSheetDisplay:', { 
    scannedSheetUrl, 
    checkName, 
    hasUrl: !!scannedSheetUrl,
    urlType: typeof scannedSheetUrl 
  });

  if (!scannedSheetUrl) {
    console.log('❌ No scanned sheet URL provided for:', checkName);
    return null; // Don't show anything if no scanned sheet
  }

  console.log('✅ Rendering scanned sheet for:', checkName, 'URL:', scannedSheetUrl);

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', color: 'warning.main' }}>
        <Box component="span" sx={{ mr: 0.5, fontSize: '16px' }}>📄</Box>
        Scanned Paper Check Sheet
        <Box 
          sx={{ 
            ml: 1, 
            px: 1, 
            py: 0.25, 
            backgroundColor: 'warning.light', 
            color: 'warning.dark', 
            borderRadius: 1, 
            fontSize: '10px',
            fontWeight: 'bold'
          }}
        >
          MANUAL CHECK
        </Box>
      </Typography>
      
      <Box
        sx={{
          position: 'relative',
          width: 250,
          height: 180,
          borderRadius: 1,
          overflow: 'hidden',
          border: '2px solid #ff9800',
          cursor: 'pointer',
          backgroundColor: '#fff8e1',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(255,152,0,0.3)',
            transform: 'scale(1.02)',
            transition: 'all 0.2s ease-in-out'
          }
        }}
        onClick={() => window.open(scannedSheetUrl, '_blank')}
        title={`Click to view scanned paper check sheet for ${checkName}`}
      >
        <img
          src={scannedSheetUrl}
          alt={`Scanned check sheet for ${checkName}`}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
          onLoad={() => {
            console.log('✅ Scanned sheet image loaded successfully:', scannedSheetUrl);
          }}
          onError={(e) => {
            console.error('❌ Failed to load scanned sheet image:', scannedSheetUrl);
            // If image fails to load, show a placeholder
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const parent = target.parentElement;
            if (parent) {
              parent.innerHTML = `
                <div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background-color: #fff8e1; color: #e65100; border: 2px dashed #ff9800;">
                  <div style="text-align: center;">
                    <div style="font-size: 24px;">📄</div>
                    <div style="font-size: 12px; font-weight: bold;">Scanned sheet unavailable</div>
                    <div style="font-size: 10px; margin-top: 4px;">URL: ${scannedSheetUrl}</div>
                  </div>
                </div>
              `;
            }
          }}
        />
        {/* Overlay indicator */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(255,152,0,0.9)',
            color: 'white',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: '10px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}
        >
          <Box component="span">🔍</Box>
          VIEW FULL SIZE
        </Box>
      </Box>
      
      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
        This check was completed using a scanned paper form. Click the image above to manually review the original paper check sheet.
      </Typography>
    </Box>
  );
};

// Component to display scribble signature
const ScribbleSignatureDisplay: React.FC<{ 
  signatureData: string;
  userName?: string;
}> = ({ signatureData, userName }) => {
  // Try to parse the signature JSON
  let signature: any = null;
  try {
    signature = JSON.parse(signatureData);
  } catch (error) {
    console.error('Failed to parse signature data:', error);
    return (
      <Box sx={{ mt: 1, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="body2" color="error">
          Invalid signature data
        </Typography>
      </Box>
    );
  }

  if (!signature.paths || !Array.isArray(signature.paths)) {
    return (
      <Box sx={{ mt: 1, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="body2" color="error">
          No signature paths found
        </Typography>
      </Box>
    );
  }

  const { width = 400, height = 200, paths, timestamp } = signature;

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', color: 'primary.main' }}>
        <EditIcon fontSize="small" sx={{ mr: 0.5 }} />
        Scribble Signature {userName && `- ${userName}`}
        {timestamp && (
          <Box 
            sx={{ 
              ml: 1, 
              px: 1, 
              py: 0.25, 
              backgroundColor: 'primary.light', 
              color: 'primary.dark', 
              borderRadius: 1, 
              fontSize: '10px',
              fontWeight: 'bold'
            }}
          >
            {new Date(timestamp).toLocaleString()}
          </Box>
        )}
      </Typography>
      
      <Box
        sx={{
          position: 'relative',
          width: Math.min(width, 300), // Scale down for display
          height: Math.min(height, 150), // Scale down for display
          borderRadius: 1,
          backgroundColor: '#fff',
          border: '2px solid #007AFF',
          padding: 1,
          cursor: 'pointer',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,122,255,0.3)',
            transform: 'scale(1.02)',
            transition: 'all 0.2s ease-in-out'
          }
        }}
        onClick={() => {
          // Create a popup to show larger version
          const popup = window.open('', '_blank', 'width=600,height=400');
          if (popup) {
            popup.document.write(`
              <html>
                <head><title>Signature - ${userName || 'User'}</title></head>
                <body style="margin: 0; padding: 20px; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f5f5f5;">
                  <div style="background: white; padding: 20px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);">
                    <h3 style="margin-top: 0; color: #333; text-align: center;">Signature - ${userName || 'User'}</h3>
                    ${timestamp ? `<p style="text-align: center; color: #666; font-size: 14px;">Signed on: ${new Date(timestamp).toLocaleString()}</p>` : ''}
                    <svg width="${width}" height="${height}" style="border: 1px solid #ddd; border-radius: 4px;">
                      ${paths.map((path: string) => 
                        `<path d="${path}" stroke="#007AFF" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" />`
                      ).join('')}
                    </svg>
                  </div>
                </body>
              </html>
            `);
          }
        }}
        title={`Click to view full size signature${userName ? ` from ${userName}` : ''}`}
      >
        <svg 
          width="100%" 
          height="100%" 
          viewBox={`0 0 ${width} ${height}`}
          style={{ display: 'block' }}
        >
          {paths.map((path: string, index: number) => (
            <path
              key={index}
              d={path}
              stroke="#007AFF"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
        </svg>
        
        {/* Overlay indicator */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(0,122,255,0.9)',
            color: 'white',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: '10px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}
        >
          <Box component="span">🔍</Box>
          VIEW FULL SIZE
        </Box>
      </Box>
      
      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
        Digital signature captured on device. Click to view full size.
      </Typography>
    </Box>
  );
};

const CheckResponses: React.FC<{ extraQuery?: Record<string, string>; groupByLoanScreens?: boolean }> = ({ extraQuery, groupByLoanScreens }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isLocationChecksView = extraQuery?.check_type === 'LOCATION';
  const isSafetyOrLocationCheck = (group: GroupedResponse) =>
    group.check_type === 'SAFETY' ||
    group.check_type === 'LOCATION' ||
    extraQuery?.check_type === 'SAFETY' ||
    extraQuery?.check_type === 'LOCATION';
  const [responses, setResponses] = useState<GroupedResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [assetFilterId, setAssetFilterId] = useState<string>('');
  const [assetsList, setAssetsList] = useState<{ asset_id: string; asset_name: string }[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedCheck, setSelectedCheck] = useState<GroupedResponse | null>(null);
  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [groupAnalysisDialogOpen, setGroupAnalysisDialogOpen] = useState(false);
  const [groupAnalysis, setGroupAnalysis] = useState<Analysis | null>(null);
  const [, setIsAnalyzing] = useState(false);
  const [outOfActionAudits, setOutOfActionAudits] = useState<Record<string, OutOfActionAudit[]>>({});
  const [loadingAudits, setLoadingAudits] = useState<Set<string>>(new Set());
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    // Default 15 per page for faster first load; user can increase via selector
    totalPages: 1,
    totalCount: 0,
    limit: 15,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [screensDialogOpen, setScreensDialogOpen] = useState(false);
  const [screensDialogGroup, setScreensDialogGroup] = useState<GroupedResponse | null>(null);
  const [screensDialogSections, setScreensDialogSections] = useState<{ screen2: Response[]; screen3: Response[]; signatures: Response[] }>({ screen2: [], screen3: [], signatures: [] });
  const [conditionImagesByType, setConditionImagesByType] = useState<Record<string, any[]>>({});
  const [loadingConditionImages, setLoadingConditionImages] = useState(false);
  const [checkAgreements, setCheckAgreements] = useState<any[]>([]);
  const [loadingAgreements, setLoadingAgreements] = useState(false);

  // DVLA dialog state
  const [dvlaDialogOpen, setDvlaDialogOpen] = useState(false);
  const [dvlaLoading, setDvlaLoading] = useState(false);
  const [dvlaError, setDvlaError] = useState<string | null>(null);
  const [dvlaData, setDvlaData] = useState<DvlaResult | null>(null);
  const [dvlaLicenceNumber, setDvlaLicenceNumber] = useState<string>('');
  const [dvlaRunning, setDvlaRunning] = useState(false);
  // Business permission: whether to show DVLA actions for loan checks
  const [allowMobileDvlaChecks, setAllowMobileDvlaChecks] = useState<boolean>(false);
  
  // AI Analysis state
  const [allowLoanReturnAIAnalysis, setAllowLoanReturnAIAnalysis] = useState<boolean>(false);
  const [aiAnalysisDialogOpen, setAiAnalysisDialogOpen] = useState(false);

  // Discarded checks toggle — hidden by default
  const [showDiscarded, setShowDiscarded] = useState(false);

  // Asset Status Changes dialog
  const [otherStatusChangesDialog, setOtherStatusChangesDialog] = useState<{
    open: boolean;
    check: GroupedResponse | null;
    audits: OutOfActionAudit[];
  }>({ open: false, check: null, audits: [] });
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [aiAnalysisError, setAiAnalysisError] = useState<string | null>(null);
  const [aiAnalysisData, setAiAnalysisData] = useState<{
    analysis_id: string;
    return_check_id: string;
    original_check_id: string;
    asset_id: string;
    should_mark_out_of_action: boolean;
    analysis_text: string;
    detailed_explanation: string;
    asset_marked_out_of_action: boolean;
    created_at: string;
  } | null>(null);

  const cleanResponseText = (text: string | undefined) => {
    if (text === undefined || text === null || text === '') return '';
    
    // Convert to string in case it's a number
    const textStr = String(text);
    
    // Debug the incoming text and surrounding context
    console.log('Raw response text:', textStr, typeof textStr);
    
    // If the response is purely numeric, return it as-is (don't strip trailing zeros)
    // This ensures responses like "0" are displayed
    if (/^\d+$/.test(textStr)) {
      return textStr;
    }
    
    // First handle simple Yes/No responses
    if (/^(Yes|No)0*$/i.test(textStr)) {
      return textStr.replace(/0+$/, '');
    }
    
    // Handle more complex cases (with additional info)
    return textStr.replace(/^(Yes|No)(\s*\d+)*/gi, '$1')  // Remove any digits after Yes/No
              .replace(/0+$/, '')                      // Remove any trailing zeros
              .trim();                                 // Clean up whitespace
  };

  // Helper function to detect if response is a scribble signature
  const isScribbleSignature = (responseText: string) => {
    try {
      const parsed = JSON.parse(responseText);
      return parsed && 
             typeof parsed === 'object' && 
             Array.isArray(parsed.paths) && 
             parsed.width && 
             parsed.height;
    } catch {
      return false;
    }
  };

  const renderResponse = (response: Response) => {
    // Log full object for debugging
    console.log('RENDERING RESPONSE:', response);
    
    // Convert has_image from numeric to boolean (0 → false, anything else → true)
    const hasImage = response.has_image ? Boolean(Number(response.has_image)) : false;
    
    // Explicitly log the additional_text field to verify it exists
    console.log('Additional text field:', response.additional_text);
    console.log('Images array:', response.images);

    // --- NEW: Handle JSON with {name, signature} where signature is base64 SVG ---
    if (typeof response.response_text === 'string') {
      try {
        const parsed = JSON.parse(response.response_text);
        if (
          parsed &&
          typeof parsed === 'object' &&
          typeof parsed.signature === 'string' &&
          parsed.signature.startsWith('data:image/svg+xml;base64,')
        ) {
          return (
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                <EditIcon fontSize="small" sx={{ mr: 0.5 }} />
                Signature (SVG){parsed.name ? ` - ${parsed.name}` : ''}
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                  width: 300,
                  height: 120,
                  borderRadius: 1,
                  backgroundColor: '#fff',
                  border: '2px solid #007AFF',
                  padding: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,122,255,0.3)',
                    transform: 'scale(1.02)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
                onClick={() => {
                  // Open SVG in a new tab
                  const popup = window.open('', '_blank', 'width=600,height=400');
                  if (popup) {
                    popup.document.write(`
                      <html>
                        <head><title>Signature - ${parsed.name || response.user_name || 'User'}</title></head>
                        <body style="margin: 0; padding: 20px; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f5f5f5;">
                          <div style="background: white; padding: 20px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);">
                            <h3 style="margin-top: 0; color: #333; text-align: center;">Signature - ${parsed.name || response.user_name || 'User'}</h3>
                            <img src='${parsed.signature}' alt="SVG Signature" style="max-width: 100%; max-height: 350px; border: 1px solid #ddd; border-radius: 4px;" />
                          </div>
                        </body>
                      </html>
                    `);
                  }
                }}
                title={`Click to view full size signature${parsed.name ? ` from ${parsed.name}` : response.user_name ? ` from ${response.user_name}` : ''}`}
              >
                <img
                  src={parsed.signature}
                  alt="SVG Signature"
                  style={{ maxWidth: '100%', maxHeight: '100%', display: 'block' }}
                />
                {/* Overlay indicator */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0,122,255,0.9)',
                    color: 'white',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '10px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <Box component="span">🔍</Box>
                  VIEW FULL SIZE
                </Box>
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                Digital signature (SVG). Click to view full size.
              </Typography>
              {/* Always show additional_text if it exists */}
              {response.additional_text && (
                <Box sx={{ mt: 1, p: 1, bgcolor: '#fff5f5', borderLeft: '4px solid #ff9800', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="warning.dark">Additional Information:</Typography>
                  <Typography variant="body2">{response.additional_text}</Typography>
                </Box>
              )}
            </Box>
          );
        }
      } catch (e) {
        // Not a JSON object, continue
      }
    }
    // --- END JSON {name, signature} SVG HANDLING ---

    // Check if this is a scribble signature
    if (isScribbleSignature(response.response_text)) {
      return (
        <Box>
          <ScribbleSignatureDisplay 
            signatureData={response.response_text}
            userName={response.user_name}
          />
          
          {/* Always show additional_text if it exists */}
          {response.additional_text && (
            <Box sx={{ mt: 1, p: 1, bgcolor: '#fff5f5', borderLeft: '4px solid #ff9800', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="warning.dark">Additional Information:</Typography>
              <Typography variant="body2">{response.additional_text}</Typography>
            </Box>
          )}
        </Box>
      );
    }

    // Clean the response text for non-signature responses
    const cleanedText = cleanResponseText(response.response_text);
    
    return (
      <Box>
        <Typography variant="body2">
          {cleanedText}
        </Typography>
        
        {/* Always show additional_text if it exists */}
        {response.additional_text && (
          <Box sx={{ mt: 1, p: 1, bgcolor: '#fff5f5', borderLeft: '4px solid #ff9800', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="warning.dark">Additional Information:</Typography>
            <Typography variant="body2">{response.additional_text}</Typography>
          </Box>
        )}
        
        {/* Display actual images if they exist */}
        {response.images && response.images.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              <PhotoIcon fontSize="small" sx={{ mr: 0.5 }} />
              Supporting Images ({response.images.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {response.images.map((image, index) => (
                <Box
                  key={image.id}
                  sx={{
                    position: 'relative',
                    width: 150,
                    height: 150,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid #e0e0e0',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                    }
                  }}
                  onClick={() => window.open(image.path, '_blank')}
                  title={`Click to view full size - Uploaded: ${new Date(image.created_at).toLocaleString()}`}
                >
                  <img
                    src={image.path}
                    alt={`Response attachment ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      // If image fails to load, show a placeholder
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                          <div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background-color: #f5f5f5; color: #666;">
                            <div style="text-align: center;">
                              <div>📷</div>
                              <div style="font-size: 12px;">Image unavailable</div>
                            </div>
                          </div>
                        `;
                      }
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}
        
        {/* Fallback: show icon if has_image is true but no images array */}
        {hasImage && (!response.images || response.images.length === 0) && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', color: 'primary.main' }}>
            <PhotoIcon fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="caption">
              Includes supporting images
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const normalizeTags = (raw: any): string[] => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map((t: any) => String(t));
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.map((t: any) => String(t)) : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const splitByLoanScreens = (group: GroupedResponse) => {
    const sections: { key: 'loan_screen_1' | 'loan_screen_2' | 'loan_screen_3' | 'other'; title: string; items: Response[] }[] = [
      { key: 'loan_screen_1', title: 'Screen 1', items: [] },
      { key: 'loan_screen_2', title: 'Screen 2', items: [] },
      { key: 'loan_screen_3', title: 'Screen 3', items: [] },
      { key: 'other', title: 'Other', items: [] },
    ];
    (group.responses || []).forEach((r) => {
      const tags = normalizeTags(r.question_tags);
      // LOAN uses loan_screen_1/2/3; LOAN_RETURN uses loan_screen1/2/3 (no underscore)
      if (tags.includes('loan_screen_1') || tags.includes('loan_screen1')) sections[0].items.push(r);
      else if (tags.includes('loan_screen_2') || tags.includes('loan_screen2')) sections[1].items.push(r);
      else if (tags.includes('loan_screen_3') || tags.includes('loan_screen3')) sections[2].items.push(r);
      else sections[3].items.push(r);
    });
    return sections.filter(s => s.items.length > 0);
  };

  const openScreensDialog = (group: GroupedResponse) => {
    const sections = splitByLoanScreens(group);
    const screen2 = sections.find(s => s.key === 'loan_screen_2')?.items || [];
    const screen3 = sections.find(s => s.key === 'loan_screen_3')?.items || [];
    const isSignature = (r: Response) => (r.question_text || '').toLowerCase().includes('signature') || (r.question_type || '').toLowerCase() === 'signature';
    const signatures = [...screen2, ...screen3].filter(isSignature);
    const screen2NoSig = screen2.filter(r => !isSignature(r));
    const screen3NoSig = screen3.filter(r => !isSignature(r));
    setScreensDialogGroup(group);
    setScreensDialogSections({ screen2: screen2NoSig, screen3: screen3NoSig, signatures });
    setScreensDialogOpen(true);

    // Fetch condition images for this safety check, grouped by image_type
    (async () => {
      try {
        setLoadingConditionImages(true);
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE_URL}/condition-images/${group.safety_check_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data?.success && Array.isArray(res.data.data)) {
          const byType: Record<string, any[]> = {};
          res.data.data.forEach((img: any) => {
            const type = (img.image_type || 'additional').toLowerCase();
            if (!byType[type]) byType[type] = [];
            // Prefer image_url; fallback to local_path
            const url = img.image_url || img.local_path;
            byType[type].push({ ...img, url });
          });
          setConditionImagesByType(byType);
        } else {
          setConditionImagesByType({});
        }
      } catch (e) {
        console.error('Failed to load condition images', e);
        setConditionImagesByType({});
      } finally {
        setLoadingConditionImages(false);
      }
    })();

    // Fetch user agreements with acceptance status for this check
    (async () => {
      try {
        setLoadingAgreements(true);
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${API_BASE_URL}/user-agreement-acceptances/check-full/${group.safety_check_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCheckAgreements(res.data?.agreements || []);
      } catch (e) {
        console.error('Failed to load user agreements', e);
        setCheckAgreements([]);
      } finally {
        setLoadingAgreements(false);
      }
    })();
  };

  const openAiAnalysisDialog = async (group: GroupedResponse) => {
    setSelectedCheck(group);
    setAiAnalysisDialogOpen(true);
    setAiAnalysisError(null);
    setAiAnalysisData(null);
    setAiAnalysisLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/safety-checks/${group.safety_check_id}/ai-analysis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data?.success) {
        setAiAnalysisData(res.data.data);
      } else {
        setAiAnalysisError(res.data?.message || 'Failed to fetch AI analysis');
      }
    } catch (e: any) {
      console.error('Failed to fetch AI analysis', e);
      setAiAnalysisError(e.response?.data?.message || 'Failed to fetch AI analysis');
    } finally {
      setAiAnalysisLoading(false);
    }
  };

  const openDvlaDialog = async (group: GroupedResponse) => {
    setSelectedCheck(group);
    setDvlaDialogOpen(true);
    setDvlaError(null);
    setDvlaData(null);
    setDvlaLoading(true);
    // Try to prefill a licence number from responses
    try {
      const candidate = (group.responses || []).find(r =>
        (r.question_id && String(r.question_id).toLowerCase().includes('dvla')) ||
        (r.question_text && r.question_text.toLowerCase().includes('licence'))
      );
      if (candidate && candidate.response_text) {
        const onlyAlnum = String(candidate.response_text).replace(/\s+/g, '').toUpperCase();
        setDvlaLicenceNumber(onlyAlnum);
      } else {
        setDvlaLicenceNumber('');
      }
    } catch {}
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/dvla/${group.safety_check_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.success) {
        setDvlaData(res.data.data as DvlaResult);
      } else {
        setDvlaError('Failed to load DVLA data');
      }
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setDvlaError(null); // No data yet; show run-check form
      } else {
        setDvlaError('Failed to load DVLA data');
      }
    } finally {
      setDvlaLoading(false);
    }
  };

  const runDvlaCheck = async () => {
    if (!selectedCheck) return;
    const number = (dvlaLicenceNumber || '').replace(/\s+/g, '').toUpperCase();
    if (!number || number.length !== 16) {
      setDvlaError('Enter a 16-character licence number');
      return;
    }
    setDvlaRunning(true);
    setDvlaError(null);
    try {
      const token = localStorage.getItem('token');
      // Always use 'live' environment - backend will enforce this anyway
      const res = await axios.post(
        `${API_BASE_URL}/dvla/${selectedCheck.safety_check_id}/check`,
        { drivingLicenceNumber: number, environment: 'live' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        // Use the normalized result from response
        setDvlaData(res.data.result as DvlaResult);
      } else {
        setDvlaError('DVLA check failed');
      }
    } catch (e: any) {
      setDvlaError(e?.response?.data?.message || 'DVLA check failed');
    } finally {
      setDvlaRunning(false);
    }
  };

  const fetchOutOfActionAudit = async (safetyCheckId: string): Promise<OutOfActionAudit[]> => {
    try {
      setLoadingAudits(prev => new Set(prev).add(safetyCheckId));
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/safety-checks/${safetyCheckId}/out-of-action-audit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = res.data?.success ? res.data.data || [] : [];
      setOutOfActionAudits(prev => ({
        ...prev,
        [safetyCheckId]: data
      }));
      return data;
    } catch (e: any) {
      console.error('Failed to load out-of-action audit logs', e);
      return [];
    } finally {
      setLoadingAudits(prev => {
        const newSet = new Set(prev);
        newSet.delete(safetyCheckId);
        return newSet;
      });
    }
  };

  const fetchResponses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      let url = `${API_BASE_URL}/check-responses`;
      const params = new URLSearchParams();
      
      if (startDate) {
        params.append('startDate', startDate.toISOString());
      }
      if (endDate) {
        params.append('endDate', endDate.toISOString());
      }
      
      // Add pagination parameters
      params.append('page', pagination.currentPage.toString());
      params.append('limit', pagination.limit.toString());
      // Append extra query filters if provided
      if (extraQuery) {
        Object.entries(extraQuery).forEach(([k, v]) => {
          if (v !== undefined && v !== null) params.append(k, String(v));
        });
      }

      // Include discarded checks when toggle is on
      if (showDiscarded) {
        params.append('include_discarded', 'true');
      }

      // Filter by asset when selected
      if (assetFilterId) {
        params.append('asset_id', assetFilterId);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const responseData = response.data.data || [];
        const paginationData = response.data.pagination;
        
        console.log('📊 CheckResponses data received:', responseData);
        console.log('📄 Pagination data:', paginationData);
        
        // Debug scanned sheet URLs and check types
        responseData.forEach((group: GroupedResponse, index: number) => {
          console.log(`📋 Group ${index + 1} - ${group.check_name}:`, {
            safety_check_id: group.safety_check_id,
            check_type: group.check_type,
            check_status: group.safety_check_status,
            asset_image_url: group.asset_image_url,
            scanned_sheet_image_url: group.scanned_sheet_image_url,
            hasScannedSheet: !!group.scanned_sheet_image_url,
            allowLoanReturnAIAnalysis: allowLoanReturnAIAnalysis,
            shouldShowAIButton: allowLoanReturnAIAnalysis && 
              (group.check_type === 'LOAN_RETURN' || group.check_type === 'loan_return') &&
              (group.safety_check_status === 'completed' || group.safety_check_status === 'FAIL' || group.safety_check_status === 'fail')
          });
        });
        
        setResponses(responseData);
        if (paginationData) {
          setPagination(paginationData);
        }
      } else {
        setError('Failed to fetch responses');
        setResponses([]);
      }
    } catch (err) {
      console.error('Error fetching responses:', err);
      setError('Failed to fetch responses. Please try again later.');
      setResponses([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, pagination.currentPage, pagination.limit, extraQuery, allowLoanReturnAIAnalysis, showDiscarded, assetFilterId]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  const deepLinkSafetyCheckId = extraQuery?.safety_check_id;

  // Open admin deep links: /admin/safety-checks?tab=responses&safety_check_id=…
  useEffect(() => {
    if (!deepLinkSafetyCheckId || loading || responses.length === 0) return;
    const found = responses.some((g) => g.safety_check_id === deepLinkSafetyCheckId);
    if (!found) return;
    setExpandedRows((prev) => new Set(prev).add(deepLinkSafetyCheckId));
    const t = window.setTimeout(() => {
      document.getElementById(`safety-check-row-${deepLinkSafetyCheckId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 350);
    return () => window.clearTimeout(t);
  }, [deepLinkSafetyCheckId, loading, responses]);

  // Fetch assets for filter dropdown (not relevant for site/location checks)
  useEffect(() => {
    if (isLocationChecksView) return;
    const fetchAssets = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API_BASE_URL}/assets`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        let list: { asset_id: string; asset_name: string }[] = [];
        if (res.data?.success && Array.isArray(res.data.data)) {
          list = res.data.data.map((a: any) => ({ asset_id: a.asset_id, asset_name: a.asset_name || a.name || a.asset_id }));
        } else if (Array.isArray(res.data)) {
          list = res.data.map((a: any) => ({ asset_id: a.asset_id, asset_name: a.asset_name || a.name || a.asset_id }));
        } else if (Array.isArray(res.data?.data)) {
          list = res.data.data.map((a: any) => ({ asset_id: a.asset_id, asset_name: a.asset_name || a.name || a.asset_id }));
        }
        list.sort((a, b) => (a.asset_name || '').localeCompare(b.asset_name || ''));
        setAssetsList(list);
      } catch (e) {
        console.error('Failed to fetch assets for filter', e);
      }
    };
    fetchAssets();
  }, [isLocationChecksView]);

  // Fetch business feature flags for Mobile DVLA checks and AI Analysis
  useEffect(() => {
    const fetchBusinessPermission = async () => {
      try {
        const token = localStorage.getItem('token');
        const businessId = localStorage.getItem('businessId');
        if (!token || !businessId) {
          setAllowMobileDvlaChecks(false);
          setAllowLoanReturnAIAnalysis(false);
          return;
        }
        const res = await axios.get(`${API_BASE_URL}/businesses/${businessId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = res.data || {};
        const toBool = (v: any) => v === 1 || v === true || v === '1';
        setAllowMobileDvlaChecks(toBool(data.allow_mobile_dvla_checks));
        setAllowLoanReturnAIAnalysis(toBool(data.allow_loan_return_ai_analysis));
        console.log('🔧 Business permissions loaded:', {
          allowMobileDvlaChecks: toBool(data.allow_mobile_dvla_checks),
          allowLoanReturnAIAnalysis: toBool(data.allow_loan_return_ai_analysis)
        });
      } catch (e) {
        console.error('Failed to fetch business permissions:', e);
        setAllowMobileDvlaChecks(false);
        setAllowLoanReturnAIAnalysis(false);
      }
    };
    fetchBusinessPermission();
  }, []);

  const handleFilter = () => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setAssetFilterId('');
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPagination(prev => ({ ...prev, currentPage: value }));
  };

  const handleLimitChange = (event: any) => {
    const newLimit = parseInt(event.target.value, 10);
    setPagination(prev => ({ 
      ...prev, 
      limit: newLimit, 
      currentPage: 1 
    }));
  };

  const toggleRow = (safetyCheckId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(safetyCheckId)) {
      newExpandedRows.delete(safetyCheckId);
    } else {
      newExpandedRows.add(safetyCheckId);
      // Fetch out-of-action audit logs when expanding
      if (!outOfActionAudits[safetyCheckId] && !loadingAudits.has(safetyCheckId)) {
        fetchOutOfActionAudit(safetyCheckId);
      }
    }
    setExpandedRows(newExpandedRows);
  };

  const handleOpenAssetStatusChanges = async (check: GroupedResponse) => {
    const safetyCheckId = check.safety_check_id;

    // Fetch audits if not already loaded
    let allAudits = outOfActionAudits[safetyCheckId] || [];
    if (allAudits.length === 0 && !loadingAudits.has(safetyCheckId)) {
      allAudits = await fetchOutOfActionAudit(safetyCheckId);
    }

    setOtherStatusChangesDialog({
      open: true,
      check,
      audits: allAudits
    });
  };

  const handleViewAnalysis = (check: GroupedResponse) => {
    setSelectedCheck(check);
    setAnalysisDialogOpen(true);
    if (check.analysis) {
      setAnalyses([check.analysis]);
    } else {
      fetchAnalyses(check.safety_check_id);
    }
  };

  const fetchAnalyses = async (safetyCheckId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/check-responses/analysis/${safetyCheckId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setAnalyses(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching analyses:', error);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for future group analysis UI
  const _handleGroupAnalysis = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates for group analysis');
      return;
    }

    try {
      setIsAnalyzing(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_BASE_URL}/check-responses/group-analyze`,
        {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setGroupAnalysis(response.data.data.analysis);
        setGroupAnalysisDialogOpen(true);
      }
    } catch (error) {
      console.error('Error performing group analysis:', error);
      setError('Failed to perform group analysis. Please try again later.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleStartDateChange = (newValue: Date | null) => {
    setStartDate(newValue);
  };

  const handleEndDateChange = (newValue: Date | null) => {
    setEndDate(newValue);
  };

  const getStatusLabel = (status: string | null | undefined): string => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'completed':   return 'Completed';
      case 'fail':        return 'Failed';
      case 'overdue':     return 'Overdue';
      case 'assigned':    return 'Assigned';
      case 'declined':    return 'Declined';
      case 'discarded':   return 'Discarded';
      case 'dismissed':   return 'Dismissed';
      case 'pending':
      case '':
      default:            return 'In Progress';
    }
  };

  const getStatusColor = (status: string | null | undefined): 'success' | 'error' | 'warning' | 'default' | 'info' => {
    const s = (status || '').toLowerCase();
    if (s === 'completed')        return 'success';
    if (s === 'fail')             return 'error';
    if (s === 'discarded' || s === 'dismissed' || s === 'declined') return 'default';
    if (s === 'overdue')          return 'error';
    return 'warning'; // in progress, pending, assigned, unknown
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Check Responses
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          {!isLocationChecksView && (
            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth>
                <InputLabel id="asset-filter-label">Asset</InputLabel>
                <Select
                  labelId="asset-filter-label"
                  label="Asset"
                  value={assetFilterId}
                  onChange={(e) => {
                    setAssetFilterId(e.target.value);
                    setPagination(prev => ({ ...prev, currentPage: 1 }));
                  }}
                >
                  <MenuItem value="">All assets</MenuItem>
                  {assetsList.map((asset) => (
                    <MenuItem key={asset.asset_id} value={asset.asset_id}>
                      {asset.asset_name || asset.asset_id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Button 
              variant="contained" 
              onClick={handleFilter}
              fullWidth={isMobile}
            >
              Filter
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button 
              variant="outlined" 
              onClick={handleClearFilters}
              fullWidth={isMobile}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Show Discarded Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showDiscarded}
              onChange={(e) => {
                setShowDiscarded(e.target.checked);
                setPagination(prev => ({ ...prev, currentPage: 1 }));
              }}
              size="small"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              Show Discarded Checks
            </Typography>
          }
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : responses.length === 0 ? (
        <Alert severity="info">
          No responses found for the selected criteria.
        </Alert>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {responses.map((group) => (
            <Paper key={group.safety_check_id} id={`safety-check-row-${group.safety_check_id}`} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {group.check_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <LocationDisplay locationName={group.location_name} latitude={group.latitude} longitude={group.longitude} />
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <IconButton
                    size="small"
                    onClick={() => toggleRow(group.safety_check_id)}
                  >
                    {expandedRows.has(group.safety_check_id) ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )}
                  </IconButton>
                  {isSafetyOrLocationCheck(group) ? (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => handleViewAnalysis(group)}
                        color="primary"
                        title="View Analysis"
                      >
                        <AssessmentIcon />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => openScreensDialog(group)}
                        color="secondary"
                        title="View Condition & Terms"
                      >
                        <ListIcon />
                      </IconButton>
                      {(() => {
                        const isLoanReturn = group.check_type === 'LOAN_RETURN' || group.check_type === 'loan_return';
                        const isCompleted = group.safety_check_status === 'completed' || group.safety_check_status === 'FAIL' || group.safety_check_status === 'fail';
                        const shouldShow = allowLoanReturnAIAnalysis && isLoanReturn && isCompleted;
                        
                        if (isLoanReturn) {
                          console.log('🔍 LOAN_RETURN check found (mobile):', {
                            check_name: group.check_name,
                            check_type: group.check_type,
                            check_status: group.safety_check_status,
                            allowLoanReturnAIAnalysis,
                            shouldShow
                          });
                        }
                        
                        return shouldShow ? (
                          <IconButton
                            size="small"
                            onClick={() => openAiAnalysisDialog(group)}
                            color="primary"
                            title="View AI Analysis Report"
                          >
                            <AssessmentIcon />
                          </IconButton>
                        ) : null;
                      })()}
                      {allowMobileDvlaChecks && (
                        <IconButton
                          size="small"
                          onClick={() => openDvlaDialog(group)}
                          color="inherit"
                          title="DVLA Data"
                        >
                          <BadgeIcon />
                        </IconButton>
                      )}
                    </>
                  )}
                  {!isLocationChecksView && (
                    <Tooltip title="Asset Status Changes" arrow>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenAssetStatusChanges(group)}
                        aria-label="Asset Status Changes"
                      >
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip 
                    label={getStatusLabel(group.safety_check_status)} 
                    color={getStatusColor(group.safety_check_status)}
                    size="small"
                  />
                  
                  {/* Remedial Work Indicator */}
                  {(() => {
                    const audits = outOfActionAudits[group.safety_check_id] || [];
                    const hasRemedialWork = audits.some(audit => 
                      audit.action === 'marked_available' && 
                      (audit.safety_check_id === group.safety_check_id || audit.related_safety_check_id === group.safety_check_id)
                    );
                    
                    if (!hasRemedialWork) return null;
                    
                    return (
                      <Chip 
                        label="Fixed" 
                        size="small" 
                        color="success"
                        icon={<Box component="span">✓</Box>}
                        onClick={() => toggleRow(group.safety_check_id)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }
                        }}
                      />
                    );
                  })()}
                </Box>
                {group.safety_check_status?.toLowerCase() === 'discarded' && group.discarded_at && (
                  <Box sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1, borderLeft: '3px solid #9e9e9e' }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Discarded:</strong> {new Date(group.discarded_at).toLocaleString()}
                    </Typography>
                    {group.assigned_to_name && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>By:</strong> {group.assigned_to_name}
                      </Typography>
                    )}
                    {group.responses.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Partial answers saved:</strong> {group.responses.length}
                      </Typography>
                    )}
                  </Box>
                )}
                <Typography variant="body2">
                  <strong>Due Date:</strong> {group.safety_check_due_date ? new Date(group.safety_check_due_date).toLocaleDateString() : 'N/A'}
                </Typography>
                {group.safety_check_status?.toLowerCase() !== 'discarded' && (
                  <Typography variant="body2">
                    <strong>Completion:</strong> {group.completed_users} / {group.total_users} users
                  </Typography>
                )}
                {!isLocationChecksView && (
                  <>
                    <AssetImageDisplay assetImageUrl={group.asset_image_url} checkName={group.check_name} isFallback={group.asset_image_is_fallback} />
                    <ScannedSheetDisplay scannedSheetUrl={group.scanned_sheet_image_url} checkName={group.check_name} />
                    {group.linked_assets && group.linked_assets.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          Also covers:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {group.linked_assets.map((a) => (
                            <Chip
                              key={a.id}
                              label={a.serial ? `${a.name} (${a.serial})` : a.name}
                              size="small"
                              variant="outlined"
                              color="info"
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </Box>
              <Collapse in={expandedRows.has(group.safety_check_id)} timeout="auto" unmountOnExit>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Responses
                  </Typography>
                  <CheckIdCopy checkId={group.check_id ?? group.safety_check_id} />
                  {!isLocationChecksView && (
                    <>
                      <AssetImageDisplay assetImageUrl={group.asset_image_url} checkName={group.check_name} isFallback={group.asset_image_is_fallback} />
                      <ScannedSheetDisplay scannedSheetUrl={group.scanned_sheet_image_url} checkName={group.check_name} />
                    </>
                  )}
                  {/* Terms & Conditions action removed as requested */}
                  {groupByLoanScreens ? (
                    splitByLoanScreens(group)
                      .filter(section => section.key === 'loan_screen_1')
                      .map(section => (
                        <Box key={section.key} sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>{section.title}</Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {section.items.map((response) => (
                              <Paper key={response.response_id} sx={{ p: 2 }}>
                                <Typography variant="body2" fontWeight="bold">{response.user_name}</Typography>
                                <Typography variant="body2" color="text.secondary">{response.question_text}</Typography>
                                {renderResponse(response)}
                                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  {/* Hide status chip for loan checks - only show overall check status */}
                                  {group.check_type !== 'LOAN' && group.check_type !== 'LOAN_RETURN' && (
                                    <Chip 
                                      label={response.response_status} 
                                      color={
                                        response.response_status?.toLowerCase() === 'complete' || 
                                        response.response_status?.toLowerCase() === 'completed' ? 'success' :
                                        response.response_status?.toLowerCase() === 'fail' ? 'error' : 'warning'
                                      }
                                      size="small"
                                    />
                                  )}
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(response.created_at).toLocaleString()}
                                  </Typography>
                                </Box>
                              </Paper>
                            ))}
                          </Box>
                        </Box>
                      ))
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {group.responses.map((response) => (
                        <Paper key={response.response_id} sx={{ p: 2 }}>
                          <Typography variant="body2" fontWeight="bold">{response.user_name}</Typography>
                          <Typography variant="body2" color="text.secondary">{response.question_text}</Typography>
                          {renderResponse(response)}
                          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            {/* Hide status chip for loan checks - only show overall check status */}
                            {group.check_type !== 'LOAN' && group.check_type !== 'LOAN_RETURN' && (
                              <Chip 
                                label={response.response_status} 
                                color={
                                  response.response_status?.toLowerCase() === 'complete' || 
                                  response.response_status?.toLowerCase() === 'completed' ? 'success' :
                                  response.response_status?.toLowerCase() === 'fail' ? 'error' : 'warning'
                                }
                                size="small"
                              />
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {new Date(response.created_at).toLocaleString()}
                            </Typography>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  )}

                  </Box>
                  <Box>
                  {/* Remedial Actions for This Check - linked to this check only */}
                  {(() => {
                    const allAudits = outOfActionAudits[group.safety_check_id] || [];
                    const isLoading = loadingAudits.has(group.safety_check_id);

                    if (isLoading) {
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" color="text.secondary">Loading…</Typography>
                        </Box>
                      );
                    }

                    // Trust the backend's filtering — it already returns only audits relevant
                    // to this check (including time-matched legacy rows with no check link).
                    const relatedAudits = allAudits;

                    if (relatedAudits.length === 0) return null;

                    const renderAuditCard = (audit: OutOfActionAudit) => {
                      const isMarkedOut = audit.action === 'marked_out_of_action';
                      const images = audit.images || [];
                      return (
                        <Paper
                          key={audit.audit_id}
                          sx={{
                            p: 1.5,
                            flex: '1 1 280px',
                            minWidth: 280,
                            borderLeft: `4px solid ${isMarkedOut ? '#ff9800' : '#4caf50'}`,
                            bgcolor: isMarkedOut ? '#fff3e0' : '#e8f5e9'
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                            <Box>
                              <Chip
                                label={isMarkedOut ? 'Marked Out of Action' : 'Marked Back in Action'}
                                color={isMarkedOut ? 'warning' : 'success'}
                                size="small"
                                sx={{ mb: 0.5 }}
                              />
                              <Typography variant="body2" fontWeight="bold">
                                {audit.first_name} {audit.last_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(audit.created_at).toLocaleString()}
                              </Typography>
                            </Box>
                            {audit.latitude && audit.longitude && (
                              <Tooltip title={`${Number(audit.latitude).toFixed(6)}, ${Number(audit.longitude).toFixed(6)}`} arrow>
                                <Chip label="📍 GPS" size="small" variant="outlined" />
                              </Tooltip>
                            )}
                          </Box>
                          {audit.note && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Note:</Typography>
                              <Typography variant="body2" sx={{ mt: 0.25 }}>{audit.note}</Typography>
                            </Box>
                          )}
                          {images.length > 0 && (
                            <Box sx={{ mt: 1.5 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                Images ({images.length}):
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {images.map((imageUrl: string, idx: number) => (
                                  <Box
                                    key={idx}
                                    sx={{ width: 100, height: 75, borderRadius: 1, overflow: 'hidden', border: '1px solid #e0e0e0', cursor: 'pointer', '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.2)' } }}
                                    onClick={() => window.open(imageUrl, '_blank')}
                                  >
                                    <img src={imageUrl} alt={`Status change ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { const t = e.target as HTMLImageElement; t.style.display = 'none'; }} />
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Paper>
                      );
                    };

                    return (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', fontWeight: 700 }}>
                          <Box component="span" sx={{ fontSize: '16px' }}>🔧</Box>
                          Remedial Actions for This Check
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1.5 }}>
                          {relatedAudits.map(renderAuditCard)}
                        </Box>
                      </Box>
                    );
                  })()}
                  </Box>
                  </Box>
                </Box>
              </Collapse>
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Check</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Completion</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {responses.map((group) => (
                <React.Fragment key={group.safety_check_id}>
                  <TableRow id={`safety-check-row-${group.safety_check_id}`}>
                    <TableCell>
                      <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => toggleRow(group.safety_check_id)}
                      >
                        {expandedRows.has(group.safety_check_id) ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      {group.check_name}
                      {group.linked_assets && group.linked_assets.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                            Also covers:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {group.linked_assets.map((a) => (
                              <Chip
                                key={a.id}
                                label={a.serial ? `${a.name} (${a.serial})` : a.name}
                                size="small"
                                variant="outlined"
                                color="info"
                                sx={{ fontSize: '0.65rem', height: 20 }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <LocationDisplay locationName={group.location_name} latitude={group.latitude} longitude={group.longitude} />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusLabel(group.safety_check_status)}
                        color={getStatusColor(group.safety_check_status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {group.safety_check_due_date ? new Date(group.safety_check_due_date).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {group.completed_users} / {group.total_users} users
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {isSafetyOrLocationCheck(group) ? (
                          <>
                            <IconButton
                              onClick={() => handleViewAnalysis(group)}
                              color="primary"
                              title="View Analysis"
                            >
                              <AssessmentIcon />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton
                              onClick={() => openScreensDialog(group)}
                              color="secondary"
                              title="View Condition & Terms"
                              sx={{ ml: 1 }}
                            >
                              <ListIcon />
                            </IconButton>
                            {(() => {
                              const isLoanReturn = group.check_type === 'LOAN_RETURN' || group.check_type === 'loan_return';
                              const isCompleted = group.safety_check_status === 'completed' || group.safety_check_status === 'FAIL' || group.safety_check_status === 'fail';
                              const shouldShow = allowLoanReturnAIAnalysis && isLoanReturn && isCompleted;
                              
                              if (isLoanReturn) {
                                console.log('🔍 LOAN_RETURN check found:', {
                                  check_name: group.check_name,
                                  check_type: group.check_type,
                                  check_status: group.safety_check_status,
                                  allowLoanReturnAIAnalysis,
                                  shouldShow
                                });
                              }
                              
                              return shouldShow ? (
                                <IconButton
                                  onClick={() => openAiAnalysisDialog(group)}
                                  color="primary"
                                  title="View AI Analysis Report"
                                  sx={{ ml: 1 }}
                                >
                                  <AssessmentIcon />
                                </IconButton>
                              ) : null;
                            })()}
                            {allowMobileDvlaChecks && (
                              <IconButton
                                onClick={() => openDvlaDialog(group)}
                                color="inherit"
                                title="DVLA Data"
                                sx={{ ml: 1 }}
                              >
                                <BadgeIcon />
                              </IconButton>
                            )}
                          </>
                        )}
                        
                        {/* Remedial Work Indicator */}
                        {!isLocationChecksView && (() => {
                          const audits = outOfActionAudits[group.safety_check_id] || [];
                          const hasRemedialWork = audits.some(audit => 
                            audit.action === 'marked_available' && 
                            (audit.safety_check_id === group.safety_check_id || audit.related_safety_check_id === group.safety_check_id)
                          );
                          
                          if (!hasRemedialWork) return null;
                          
                          return (
                            <Tooltip title="Remedial work completed - click to view" arrow>
                              <Chip 
                                label="Fixed" 
                                size="small" 
                                color="success"
                                icon={<Box component="span">✓</Box>}
                                onClick={() => toggleRow(group.safety_check_id)}
                                sx={{ 
                                  ml: 1, 
                                  cursor: 'pointer',
                                  '&:hover': {
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                  }
                                }}
                              />
                            </Tooltip>
                          );
                        })()}

                        {!isLocationChecksView && (
                          <Tooltip title="Asset Status Changes" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenAssetStatusChanges(group)}
                              sx={{ ml: 0.5 }}
                              aria-label="Asset Status Changes"
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                      <Collapse in={expandedRows.has(group.safety_check_id)} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box>
                          <Typography variant="h6" gutterBottom component="div">Responses</Typography>
                          <CheckIdCopy checkId={group.check_id ?? group.safety_check_id} />
                          {!isLocationChecksView && (
                            <>
                              <AssetImageDisplay assetImageUrl={group.asset_image_url} checkName={group.check_name} isFallback={group.asset_image_is_fallback} />
                              <ScannedSheetDisplay scannedSheetUrl={group.scanned_sheet_image_url} checkName={group.check_name} />
                            </>
                          )}
                          {/* Terms & Conditions action removed as requested */}
                          {groupByLoanScreens ? (
                            splitByLoanScreens(group)
                              .filter(section => section.key === 'loan_screen_1')
                              .map(section => (
                                <Box key={section.key} sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{section.title}</Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>User</TableCell>
                                        <TableCell>Question</TableCell>
                                        <TableCell>Response</TableCell>
                                        {/* Hide Status column for loan checks */}
                                        {group.check_type !== 'LOAN' && group.check_type !== 'LOAN_RETURN' && (
                                          <TableCell>Status</TableCell>
                                        )}
                                        <TableCell>Created At</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {section.items.map((response) => (
                                        <TableRow key={response.response_id}>
                                          <TableCell>{response.user_name}</TableCell>
                                          <TableCell>{response.question_text}</TableCell>
                                          <TableCell><Box>{renderResponse(response)}</Box></TableCell>
                                          {/* Hide Status cell for loan checks */}
                                          {group.check_type !== 'LOAN' && group.check_type !== 'LOAN_RETURN' && (
                                            <TableCell>
                                              <Chip 
                                                label={response.response_status} 
                                                color={
                                                  response.response_status?.toLowerCase() === 'complete' || 
                                                  response.response_status?.toLowerCase() === 'completed' ? 'success' :
                                                  response.response_status?.toLowerCase() === 'fail' ? 'error' : 'warning'
                                                }
                                                size="small"
                                              />
                                            </TableCell>
                                          )}
                                          <TableCell>{new Date(response.created_at).toLocaleString()}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              ))
                          ) : (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>User</TableCell>
                                  <TableCell>Question</TableCell>
                                  <TableCell>Response</TableCell>
                                  {/* Hide Status column for loan checks */}
                                  {group.check_type !== 'LOAN' && group.check_type !== 'LOAN_RETURN' && (
                                    <TableCell>Status</TableCell>
                                  )}
                                  <TableCell>Created At</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {group.responses.map((response) => (
                                  <TableRow key={response.response_id}>
                                    <TableCell>{response.user_name}</TableCell>
                                    <TableCell>{response.question_text}</TableCell>
                                    <TableCell><Box>{renderResponse(response)}</Box></TableCell>
                                    {/* Hide Status cell for loan checks */}
                                    {group.check_type !== 'LOAN' && group.check_type !== 'LOAN_RETURN' && (
                                      <TableCell>
                                        <Chip 
                                          label={response.response_status} 
                                          color={
                                            response.response_status?.toLowerCase() === 'complete' || 
                                            response.response_status?.toLowerCase() === 'completed' ? 'success' :
                                            response.response_status?.toLowerCase() === 'fail' ? 'error' : 'warning'
                                          }
                                          size="small"
                                        />
                                      </TableCell>
                                    )}
                                    <TableCell>{new Date(response.created_at).toLocaleString()}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                          
                          </Box>
                          <Box>
                          {/* Remedial Actions for This Check - linked to this check only */}
                          {(() => {
                            const allAudits = outOfActionAudits[group.safety_check_id] || [];
                            const isLoading = loadingAudits.has(group.safety_check_id);

                            if (isLoading) {
                              return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1 }}>
                                  <CircularProgress size={16} />
                                  <Typography variant="body2" color="text.secondary">Loading…</Typography>
                                </Box>
                              );
                            }

                            // Trust the backend's filtering — it already returns only audits relevant
                            // to this check (including time-matched legacy rows with no check link).
                            const relatedAudits = allAudits;

                            if (relatedAudits.length === 0) return null;

                            const renderAuditCard = (audit: OutOfActionAudit) => {
                              const isMarkedOut = audit.action === 'marked_out_of_action';
                              const images = audit.images || [];
                              return (
                                <Paper
                                  key={audit.audit_id}
                                  sx={{
                                    p: 1.5,
                                    flex: '1 1 280px',
                                    minWidth: 280,
                                    borderLeft: `4px solid ${isMarkedOut ? '#ff9800' : '#4caf50'}`,
                                    bgcolor: isMarkedOut ? '#fff3e0' : '#e8f5e9'
                                  }}
                                >
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                    <Box>
                                      <Chip
                                        label={isMarkedOut ? 'Marked Out of Action' : 'Marked Back in Action'}
                                        color={isMarkedOut ? 'warning' : 'success'}
                                        size="small"
                                        sx={{ mb: 0.5 }}
                                      />
                                      <Typography variant="body2" fontWeight="bold">
                                        {audit.first_name} {audit.last_name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {new Date(audit.created_at).toLocaleString()}
                                      </Typography>
                                    </Box>
                                    {audit.latitude && audit.longitude && (
                                      <Tooltip title={`${Number(audit.latitude).toFixed(6)}, ${Number(audit.longitude).toFixed(6)}`} arrow>
                                        <Chip label="📍 GPS" size="small" variant="outlined" />
                                      </Tooltip>
                                    )}
                                  </Box>
                                  {audit.note && (
                                    <Box sx={{ mt: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Note:</Typography>
                                      <Typography variant="body2" sx={{ mt: 0.25 }}>{audit.note}</Typography>
                                    </Box>
                                  )}
                                  {images.length > 0 && (
                                    <Box sx={{ mt: 1.5 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                        Images ({images.length}):
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {images.map((imageUrl: string, idx: number) => (
                                          <Box
                                            key={idx}
                                            sx={{ width: 100, height: 75, borderRadius: 1, overflow: 'hidden', border: '1px solid #e0e0e0', cursor: 'pointer', '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.2)' } }}
                                            onClick={() => window.open(imageUrl, '_blank')}
                                          >
                                            <img src={imageUrl} alt={`Status change ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { const t = e.target as HTMLImageElement; t.style.display = 'none'; }} />
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>
                                  )}
                                </Paper>
                              );
                            };

                            return (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', fontWeight: 700 }}>
                                  <Box component="span" sx={{ fontSize: '16px' }}>🔧</Box>
                                  Remedial Actions for This Check
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1.5 }}>
                                  {relatedAudits.map(renderAuditCard)}
                                </Box>
                              </Box>
                            );
                          })()}
                          </Box>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination Controls */}
      {responses.length > 0 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: 'center',
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} of{' '}
                {pagination.totalCount} results
              </Typography>
              
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Per Page</InputLabel>
                <Select
                  value={pagination.limit}
                  onChange={handleLimitChange}
                  label="Per Page"
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Pagination
              count={pagination.totalPages}
              page={pagination.currentPage}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? "small" : "medium"}
              showFirstButton
              showLastButton
            />
          </Box>
        </Paper>
      )}

      {/* Asset Status Changes dialog */}
      <Dialog
        open={otherStatusChangesDialog.open}
        onClose={() => setOtherStatusChangesDialog({ open: false, check: null, audits: [] })}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="action" />
          All Asset Status Changes
          {otherStatusChangesDialog.check && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
              — {otherStatusChangesDialog.check.check_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {otherStatusChangesDialog.audits.length === 0 ? (
            <Alert severity="info">No asset status changes have been recorded for this asset.</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {otherStatusChangesDialog.audits.map((audit: OutOfActionAudit) => {
                const isMarkedOut = audit.action === 'marked_out_of_action';
                const images = audit.images || [];
                return (
                  <Paper
                    key={audit.audit_id}
                    sx={{
                      p: 2,
                      borderLeft: `4px solid ${isMarkedOut ? '#ff9800' : '#4caf50'}`,
                      bgcolor: isMarkedOut ? '#fff3e0' : '#e8f5e9'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Chip
                          label={isMarkedOut ? 'Marked Out of Action' : 'Marked Back in Action'}
                          color={isMarkedOut ? 'warning' : 'success'}
                          size="small"
                          sx={{ mb: 1 }}
                        />
                        <Typography variant="body2" fontWeight="bold">
                          {audit.first_name} {audit.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(audit.created_at).toLocaleString()}
                        </Typography>
                      </Box>
                      {audit.latitude && audit.longitude && (
                        <Tooltip title={`Location: ${Number(audit.latitude).toFixed(6)}, ${Number(audit.longitude).toFixed(6)}`} arrow>
                          <Chip label="GPS" size="small" variant="outlined" />
                        </Tooltip>
                      )}
                    </Box>
                    {audit.note && (
                      <Box sx={{ mt: 1, p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Note:</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>{audit.note}</Typography>
                      </Box>
                    )}
                    {images.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                          Attached Images ({images.length}):
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {images.map((imageUrl: string, idx: number) => (
                            <Box
                              key={idx}
                              sx={{
                                width: 120,
                                height: 90,
                                borderRadius: 1,
                                overflow: 'hidden',
                                border: '1px solid #e0e0e0',
                                cursor: 'pointer',
                                '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }
                              }}
                              onClick={() => window.open(imageUrl, '_blank')}
                              title="Click to view full size"
                            >
                              <img
                                src={imageUrl}
                                alt={`Status change ${idx + 1}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  const parent = target.parentElement;
                                  if (parent) {
                                    parent.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; background: #f0f0f0; color: #999;">Image unavailable</div>`;
                                  }
                                }}
                              />
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOtherStatusChangesDialog({ open: false, check: null, audits: [] })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={analysisDialogOpen}
        onClose={() => setAnalysisDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          AI Analysis - {selectedCheck?.check_name}
        </DialogTitle>
        <DialogContent>
          {analyses.length === 0 ? (
            <Alert severity="info">No analysis available yet</Alert>
          ) : (
            <Box sx={{ mt: 2 }}>
              {analyses.map((analysis) => (
                <Paper key={analysis.created_at} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {new Date(analysis.created_at).toLocaleString()}
                  </Typography>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                    {analysis.analysis_text}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={() => setAnalysisDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={groupAnalysisDialogOpen}
        onClose={() => setGroupAnalysisDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Group Analysis Report
          {startDate && endDate && (
            <Typography variant="subtitle2" color="text.secondary">
              {startDate.toLocaleDateString()} to {endDate.toLocaleDateString()}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {groupAnalysis ? (
            <Box sx={{ mt: 2 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {new Date(groupAnalysis.created_at).toLocaleString()}
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                  {groupAnalysis.analysis_text}
                </Typography>
              </Paper>
            </Box>
          ) : (
            <Alert severity="info">No analysis available yet</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={() => setGroupAnalysisDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={screensDialogOpen}
        onClose={() => setScreensDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Condition & Terms — {screensDialogGroup?.check_name}
          <Typography variant="subtitle2" color="text.secondary">
            {screensDialogGroup?.location_name}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setScreensDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Condition</Typography>
              {screensDialogSections.screen2.length === 0 ? (
                <Alert severity="info">No condition items.</Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {screensDialogSections.screen2.map(r => (
                    <Paper key={r.response_id} sx={{ p: 1.5 }}>
                      <Typography variant="subtitle2">{r.question_text}</Typography>
                      <Box sx={{ mt: 0.5 }}>{renderResponse(r)}</Box>
                      <Typography variant="caption" color="text.secondary">{new Date(r.created_at).toLocaleString()}</Typography>
                    </Paper>
                  ))}
                </Box>
              )}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Condition Images</Typography>
                {loadingConditionImages ? (
                  <CircularProgress size={24} />
                ) : (
                  <Stack spacing={1.5}>
                    {['front', 'back', 'side', 'additional'].map(type => (
                      <Box key={type}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>{type}</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                          {(conditionImagesByType[type] || []).length === 0 ? (
                            <Typography variant="caption" color="text.secondary">No images</Typography>
                          ) : (
                            (conditionImagesByType[type] || []).map(img => (
                              <Box key={img.condition_image_id} sx={{ width: 120, height: 90, borderRadius: 1, overflow: 'hidden', border: '1px solid #e0e0e0', cursor: 'pointer' }}
                                   onClick={() => window.open(img.url, '_blank')}
                                   title={`${type} • ${new Date(img.created_at).toLocaleString()}`}>
                                <img src={img.url} alt={`${type}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              </Box>
                            ))
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Grid>
            {screensDialogGroup?.check_type !== 'LOAN_RETURN' && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Terms</Typography>
                {screensDialogSections.screen3.length === 0 ? (
                  <Alert severity="info">No terms items.</Alert>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {screensDialogSections.screen3.map(r => (
                      <Paper key={r.response_id} sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2">{r.question_text}</Typography>
                        <Box sx={{ mt: 0.5 }}>{renderResponse(r)}</Box>
                        <Typography variant="caption" color="text.secondary">{new Date(r.created_at).toLocaleString()}</Typography>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Grid>
            )}
            {screensDialogGroup?.check_type !== 'LOAN_RETURN' && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Signatures</Typography>
                {screensDialogSections.signatures.length === 0 ? (
                  <Alert severity="info">No signatures found.</Alert>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {screensDialogSections.signatures.map(r => (
                      <Paper key={r.response_id} sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2">{r.question_text}</Typography>
                        <Box sx={{ mt: 0.5 }}>{renderResponse(r)}</Box>
                        <Typography variant="caption" color="text.secondary">{new Date(r.created_at).toLocaleString()}</Typography>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Grid>
            )}

            {/* User Agreements section - only for initial loan checks, not return checks */}
            {screensDialogGroup?.check_type !== 'LOAN_RETURN' && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">User Agreements</Typography>
                {loadingAgreements && <CircularProgress size={14} />}
              </Box>
              {!loadingAgreements && checkAgreements.length === 0 ? (
                <Alert severity="info">No user agreements on this template.</Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {checkAgreements.map((ag: any) => {
                    const accepted = !!ag.acceptance_id;
                    return (
                      <Paper
                        key={ag.agreement_id}
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderColor: accepted ? 'success.main' : ag.is_required ? 'warning.main' : 'divider',
                          borderWidth: accepted || ag.is_required ? 2 : 1,
                          borderRadius: 2,
                        }}
                      >
                        {/* Header row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {ag.agreement_title}
                            </Typography>
                            {ag.is_required ? (
                              <Box component="span" sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'warning.dark', bgcolor: 'warning.lighter', px: 0.8, py: 0.2, borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
                                REQUIRED
                              </Box>
                            ) : (
                              <Box component="span" sx={{ fontSize: '0.65rem', color: 'text.secondary', bgcolor: 'action.hover', px: 0.8, py: 0.2, borderRadius: 1 }}>
                                optional
                              </Box>
                            )}
                          </Box>
                          {/* Acceptance badge */}
                          {accepted ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
                              <CheckCircleIcon fontSize="small" />
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.main' }}>
                                Accepted {ag.accepted_at ? `• ${new Date(ag.accepted_at).toLocaleString()}` : ''}
                              </Typography>
                              {ag.has_signature ? (
                                <Box component="span" sx={{ ml: 0.5, fontSize: '0.65rem', color: 'text.secondary' }}>✍️ Signed</Box>
                              ) : null}
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: ag.is_required ? 'warning.dark' : 'text.secondary' }}>
                              <CancelIcon fontSize="small" />
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                Not accepted
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Agreement content */}
                        {(ag.agreement_type === 'text' || ag.agreement_type === 'text_with_mergetags') && ag.agreement_content && (
                          <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, maxHeight: 160, overflowY: 'auto' }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.78rem', lineHeight: 1.6, color: 'text.secondary' }}>
                              {ag.agreement_content}
                            </Typography>
                          </Box>
                        )}

                        {ag.agreement_type === 'pdf' && (
                          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PictureAsPdfIcon fontSize="small" color="error" />
                            {ag.pdf_url ? (
                              <Typography
                                variant="body2"
                                component="a"
                                href={ag.pdf_url}
                                target="_blank"
                                rel="noreferrer"
                                sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.82rem' }}
                              >
                                {ag.pdf_filename || 'Download PDF'}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
                                {ag.pdf_filename || 'PDF agreement'}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScreensDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Terms & Conditions dialog removed */}
      <Dialog
        open={dvlaDialogOpen}
        onClose={() => setDvlaDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          DVLA Data — {selectedCheck?.check_name}
          <Typography variant="subtitle2" color="text.secondary">
            {selectedCheck?.location_name}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setDvlaDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {dvlaLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : dvlaData ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Points Summary - Moved to top with prominent styling */}
              <Paper sx={{ 
                p: 2, 
                background: dvlaData.pointsTotal > 0 ? 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)' : 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
                border: dvlaData.pointsTotal > 0 ? '2px solid #ff9800' : '2px solid #4caf50',
                borderRadius: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: dvlaData.pointsTotal > 0 ? '#e65100' : '#2e7d32' }}>
                    Penalty Points
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    backgroundColor: dvlaData.pointsTotal > 0 ? '#ff9800' : '#4caf50',
                    color: 'white',
                    px: 2,
                    py: 1,
                    borderRadius: 3,
                    fontSize: '1.2rem',
                    fontWeight: 'bold'
                  }}>
                    {dvlaData.pointsTotal} {dvlaData.pointsTotal === 1 ? 'Point' : 'Points'}
                  </Box>
                </Box>
                {dvlaData.endorsements.length > 0 && (
                  <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                    {dvlaData.endorsements.length} active endorsement{dvlaData.endorsements.length === 1 ? '' : 's'}
                  </Typography>
                )}
              </Paper>

              {/* Driver Information */}
              <Paper sx={{ p: 2, background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#7b1fa2', mb: 1.5, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ mr: 1, fontSize: '20px' }}>👤</Box>
                  Driver Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{dvlaData.driver.name || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{dvlaData.driver.dob || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Gender</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{dvlaData.driver.gender || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Licence Number</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium', fontFamily: 'monospace' }}>{dvlaData.driver.licenseNumber || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Licence Status */}
              <Paper sx={{ p: 2, background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 1.5, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ mr: 1, fontSize: '20px' }}>📄</Box>
                  Licence Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Type</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{dvlaData.licence.type || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip 
                      label={dvlaData.licence.status || 'N/A'} 
                      color={dvlaData.licence.status === 'Valid' ? 'success' : 'default'}
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Issue Number</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{dvlaData.licence.issueNumber || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Valid Period</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {dvlaData.licence.validFrom || 'N/A'} → {dvlaData.licence.validUntil || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Address */}
              <Paper sx={{ p: 2, background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#f57f17', mb: 1.5, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ mr: 1, fontSize: '20px' }}>📍</Box>
                  Address
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', lineHeight: 1.6 }}>
                  {[dvlaData.address.line1, dvlaData.address.line2, dvlaData.address.line3, dvlaData.address.line4, dvlaData.address.line5].filter(Boolean).join(', ') || 'N/A'}
                </Typography>
                {dvlaData.address.postcode && (
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mt: 1, color: '#f57f17' }}>
                    {dvlaData.address.postcode}
                  </Typography>
                )}
              </Paper>

              {/* Vehicle Categories */}
              <Paper sx={{ p: 2, background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#c2185b', mb: 1.5, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ mr: 1, fontSize: '20px' }}>•</Box>
                  Vehicle Categories ({dvlaData.vehicleCategories.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                  {dvlaData.vehicleCategories.map(vc => (
                    <Box key={`${vc.code}-${vc.fromDate}`} sx={{ 
                      p: 2, 
                      background: 'rgba(255,255,255,0.8)', 
                      border: '1px solid #e91e63', 
                      borderRadius: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(233,30,99,0.2)',
                        transform: 'translateY(-1px)'
                      }
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#c2185b' }}>
                        {vc.code} — {vc.description}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Valid: {vc.fromDate || '—'} to {vc.expiryDate || 'No expiry'}
                      </Typography>
                      {vc.restrictions && vc.restrictions.length > 0 && (
                        <Box sx={{ mt: 1, p: 1, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc02' }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                            Restrictions: {vc.restrictions.map(r => `${r.code}${r.literal ? ` (${r.literal})` : ''}`).join(', ')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Paper>

              {/* Endorsements */}
              <Paper sx={{ p: 2, background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#d32f2f', mb: 1.5, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ mr: 1, fontSize: '20px' }}>⚠️</Box>
                  Endorsements ({dvlaData.endorsements.length})
                </Typography>
                {dvlaData.endorsements.length === 0 ? (
                  <Box sx={{ 
                    p: 2, 
                    background: 'rgba(76,175,80,0.1)', 
                    border: '1px solid #4caf50', 
                    borderRadius: 2,
                    textAlign: 'center'
                  }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                      ✅ No Active Endorsements
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                    {dvlaData.endorsements.map(e => (
                      <Box key={`${e.code}-${e.offenseDate}-${e.courtReference}`} sx={{ 
                        p: 2, 
                        background: 'rgba(255,255,255,0.8)', 
                        border: '1px solid #f44336', 
                        borderRadius: 2,
                        borderLeft: '4px solid #f44336'
                      }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                          {e.code} — {e.offenseLegalLiteral}
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                          <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary">Offence Date</Typography>
                            <Typography variant="body2">{e.offenseDate || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary">Conviction</Typography>
                            <Typography variant="body2">{e.convictionDate || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary">Points</Typography>
                            <Chip 
                              label={`${e.points} pts`} 
                              color="error" 
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Grid>
                        </Grid>
                        {(e.disqualificationType || e.disqualificationYears) && (
                          <Box sx={{ mt: 1, p: 1, bgcolor: '#ffebee', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                              Disqualification: {e.disqualificationType || 'N/A'} {e.disqualificationYears ? `(${e.disqualificationYears} years)` : ''}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {dvlaError && <Alert severity="error">{dvlaError}</Alert>}
              <Alert severity="info">No DVLA data found for this check. Run a DVLA check:</Alert>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Driving Licence Number"
                    value={dvlaLicenceNumber}
                    onChange={(e) => setDvlaLicenceNumber(e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="e.g. HARTW123456J99AA"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" onClick={runDvlaCheck} disabled={dvlaRunning}>
                    {dvlaRunning ? <CircularProgress size={24} /> : 'Run DVLA Check'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDvlaDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* AI Analysis Dialog */}
      <Dialog
        open={aiAnalysisDialogOpen}
        onClose={() => setAiAnalysisDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">AI Analysis Report</Typography>
            <IconButton
              aria-label="close"
              onClick={() => setAiAnalysisDialogOpen(false)}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {aiAnalysisLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : aiAnalysisError ? (
            <Alert severity="error">{aiAnalysisError}</Alert>
          ) : aiAnalysisData ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Status Summary */}
              <Paper sx={{ p: 2, bgcolor: aiAnalysisData.should_mark_out_of_action ? '#ffebee' : '#e8f5e9', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Chip
                    label={aiAnalysisData.should_mark_out_of_action ? 'MARK OUT OF ACTION' : 'NO ACTION REQUIRED'}
                    color={aiAnalysisData.should_mark_out_of_action ? 'error' : 'success'}
                    sx={{ fontWeight: 'bold' }}
                  />
                  {aiAnalysisData.asset_marked_out_of_action && (
                    <Chip
                      label="Asset Marked Out of Action"
                      color="warning"
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Analysis Date: {new Date(aiAnalysisData.created_at).toLocaleString()}
                </Typography>
              </Paper>

              {/* Detailed Explanation */}
              {aiAnalysisData.detailed_explanation && (
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Detailed Analysis
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.6
                    }}
                  >
                    {aiAnalysisData.detailed_explanation}
                  </Typography>
                </Paper>
              )}

            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiAnalysisDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CheckResponses; 